import React, { useEffect, useState } from "react";
import { api } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/shared";
import {
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  ShieldAlert,
  LogOut,
  Check,
} from "lucide-react";

// -------------------------------------------------------------------------
// User-Agent → friendly device label
// -------------------------------------------------------------------------
// Tiny inline parser — we don't need ua-parser-js's full feature set, just
// enough to render "Chrome on macOS" / "Mobile Safari on iPhone".
function parseUserAgent(ua) {
  if (!ua) return { device: "Unknown device", icon: "globe" };
  const lower = ua.toLowerCase();

  // OS / device family
  let os = "Unknown OS";
  let icon = "monitor";
  if (/iphone|ipod/.test(lower)) {
    os = "iPhone";
    icon = "smartphone";
  } else if (/ipad/.test(lower)) {
    os = "iPad";
    icon = "tablet";
  } else if (/android/.test(lower)) {
    os = /mobile/.test(lower) ? "Android phone" : "Android tablet";
    icon = /mobile/.test(lower) ? "smartphone" : "tablet";
  } else if (/mac os x|macintosh/.test(lower)) {
    os = "Mac";
  } else if (/windows/.test(lower)) {
    os = "Windows";
  } else if (/linux/.test(lower)) {
    os = "Linux";
  } else if (/cros/.test(lower)) {
    os = "ChromeOS";
  }

  // Browser — order matters: check for more specific UAs first
  let browser = "browser";
  if (/edg\//.test(lower)) browser = "Edge";
  else if (/opr\//.test(lower) || /opera/.test(lower)) browser = "Opera";
  else if (/firefox/.test(lower)) browser = "Firefox";
  else if (/chrome/.test(lower) && !/chromium/.test(lower)) browser = "Chrome";
  else if (/safari/.test(lower)) browser = "Safari";
  else if (/curl/.test(lower)) browser = "curl";

  return { device: `${browser} on ${os}`, icon };
}

const ICONS = { monitor: Monitor, smartphone: Smartphone, tablet: Tablet, globe: Globe };

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 0) {
    // future → "in N days" (used for "expires in")
    const abs = Math.abs(diffSec);
    if (abs < 60) return "in <1 min";
    if (abs < 3600) return `in ${Math.round(abs / 60)} min`;
    if (abs < 86400) return `in ${Math.round(abs / 3600)} h`;
    return `in ${Math.round(abs / 86400)} days`;
  }
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} days ago`;
}

export default function ActiveSessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingHandle, setRevokingHandle] = useState(null);
  const [confirmRevokeAllOpen, setConfirmRevokeAllOpen] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = async () => {
    try {
      const r = await api.get(`/auth/sessions`);
      setSessions(r.data.sessions || []);
    } catch (e) {
      // Older sessions (pre-JWT) may not validate via Bearer — silently empty.
      if (e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Failed to load active sessions");
      }
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const revokeOne = async (handle) => {
    setRevokingHandle(handle);
    try {
      await api.delete(`/auth/sessions/${handle}`);
      toast.success("Device signed out");
      await fetchSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke session");
    } finally {
      setRevokingHandle(null);
    }
  };

  const revokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      const r = await api.delete(`/auth/sessions/others`);
      toast.success(`Signed out ${r.data.revoked} other device${r.data.revoked === 1 ? "" : "s"}`);
      await fetchSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke other sessions");
    } finally {
      setRevokingAll(false);
      setConfirmRevokeAllOpen(false);
    }
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <div className="space-y-4" data-testid="active-sessions-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Active Sessions</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Devices currently signed in to your account. Sign out anything you don&apos;t recognize.
          </p>
        </div>
        {otherCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"
            onClick={() => setConfirmRevokeAllOpen(true)}
            disabled={revokingAll}
            data-testid="active-sessions-revoke-all-btn"
          >
            <ShieldAlert className="h-4 w-4 mr-1.5" />
            Sign out all other devices
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading sessions…</span>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-4 text-center">
          No active sessions found.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {sessions.map((s) => {
            const { device, icon } = parseUserAgent(s.user_agent);
            const Icon = ICONS[icon] || Globe;
            return (
              <li
                key={s.handle}
                className={`flex items-center gap-3 px-4 py-3 ${
                  s.is_current ? "bg-emerald-50/40" : "bg-white"
                }`}
                data-testid={`active-session-row-${s.handle}`}
              >
                <Icon className="h-5 w-5 text-gray-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{device}</span>
                    {s.is_current && (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"
                        data-testid="active-session-current-badge"
                      >
                        <Check className="h-3 w-3" /> This device
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    {s.ip_address && <span>IP {s.ip_address}</span>}
                    <span>Signed in {relativeTime(s.created_at)}</span>
                    {s.expires_at && <span>Expires {relativeTime(s.expires_at)}</span>}
                  </div>
                </div>
                {!s.is_current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                    onClick={() => revokeOne(s.handle)}
                    disabled={revokingHandle === s.handle}
                    data-testid={`active-session-revoke-btn-${s.handle}`}
                  >
                    {revokingHandle === s.handle ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 mr-1" />
                        Sign out
                      </>
                    )}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmActionDialog
        open={confirmRevokeAllOpen}
        onOpenChange={setConfirmRevokeAllOpen}
        title="Sign out all other devices?"
        description={
          <>
            You&apos;ll stay signed in on this device. {otherCount} other device
            {otherCount === 1 ? "" : "s"} will be signed out immediately and need to sign
            in again.
          </>
        }
        variant="danger"
        confirmLabel="Sign out other devices"
        busyLabel="Signing out…"
        loading={revokingAll}
        onConfirm={revokeAllOthers}
        testIdPrefix="active-sessions-confirm-revoke-all"
      />
    </div>
  );
}
